/**
 * GraphQL queries and hooks — businessObjective pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';
import type {
  BusinessObjective,
  BusinessObjectiveConnection,
  BusinessObjectiveFilter,
} from '../types/businessObjective';

export const BUSINESS_OBJECTIVE_FIELDS = gql`
  fragment BusinessObjectiveFields on BusinessObjective {
    entityId
    entityType
    name
    description
    objectiveType
    priority
    status
    startDate
    targetDate
    timeHorizon
    successCriteria
    kpiStatement
    metrics {
      name
      baseline
      target
      current
      unit
      measuredAt
    }
    ownerId
    owningBusinessUnitId
    sponsorId
    tags
    notes
    _schemaVersion
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
    _deletedAt
  }
`;

export const GET_BUSINESS_OBJECTIVE = gql`
  ${BUSINESS_OBJECTIVE_FIELDS}
  query GetBusinessObjective($id: ID!) {
    businessObjective(id: $id) {
      ...BusinessObjectiveFields
    }
  }
`;

export const LIST_BUSINESS_OBJECTIVES = gql`
  ${BUSINESS_OBJECTIVE_FIELDS}
  query ListBusinessObjectives($filter: BusinessObjectiveFilter, $first: Int, $after: String) {
    businessObjectives(filter: $filter, first: $first, after: $after) {
      edges {
        node {
          ...BusinessObjectiveFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export function useBusinessObjective(
  id: string
): QueryResult<{ businessObjective: BusinessObjective | null }> {
  return useQuery(GET_BUSINESS_OBJECTIVE, {
    variables: { id },
    skip: !id,
  });
}

export function useBusinessObjectives(
  filter?: BusinessObjectiveFilter,
  first: number = 50
): QueryResult<{ businessObjectives: BusinessObjectiveConnection }> {
  return useQuery(LIST_BUSINESS_OBJECTIVES, {
    variables: { filter, first },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}
